import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button, AlertMessage } from "components/atoms";
import { PasswordInput, TextInput } from "components/molecules";
import { useAuth } from "contexts";

interface ResetPasswordParams {
  username: string;
  confirmationCode: string;
  newPassword: string;
}

const ConfirmResetPasswordSchema = Yup.object().shape({
  resetPasswordCode: Yup.string()
    .max(6, "Verification code must be 6 characters")
    .required("Verification code is required"),
  newPassword: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(
      /[!@#$%^&*(),.?":{}|<>]/,
      "Password must contain at least one special character"
    )
    .required("Password is required"),
  confirmNewPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), undefined], "Passwords must match")
    .required("Confirm password is required"),
});

const ConfirmResetPasswordForm: React.FC = () => {
  const {
    authState,
    setAuthState,
    handleConfirmResetPassword,
    handleResetPassword,
  } = useAuth();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSuccessfulReset = (): void => {
    confirmResetPasswordFormik.resetForm();
    setAuthState((prevState) => ({
      ...prevState,
      nextStep: "SIGN_IN",
      temporaryData: undefined,
    }));
  };

  const handleSuccessfulResend = (): void => {
    setAuthState((prevState) => ({
      ...prevState,
    }));
  };

  const handleResetError = (error: unknown): void => {
    const errorMessage = error instanceof Error ? error.message : String(error);
    setErrorMessage(errorMessage);
  };

  const handleConfirmResetPasswordSubmit = async (values: {
    resetPasswordCode: string;
    newPassword: string;
  }) => {
    const username = authState?.temporaryData?.username;

    if (!username) {
      setErrorMessage("Username is required. Please try again.");
      return;
    }

    const resetPasswordParams: ResetPasswordParams = {
      username: username!,
      confirmationCode: values.resetPasswordCode,
      newPassword: values.newPassword,
    };

    try {
      const error = await handleConfirmResetPassword(resetPasswordParams);

      if (error) {
        handleResetError(error);
        return;
      }

      handleSuccessfulReset();
    } catch (error) {
      handleResetError(error);
    }
  };

  const confirmResetPasswordFormik = useFormik({
    initialValues: {
      resetPasswordCode: "",
      newPassword: "",
      confirmNewPassword: "",
    },
    validationSchema: ConfirmResetPasswordSchema,
    onSubmit: handleConfirmResetPasswordSubmit,
  });

  const resendCode = async (): Promise<void> => {
    const username = authState?.temporaryData?.username;

    if (!username) {
      setErrorMessage("Please try again.");
      return;
    }

    try {
      const error = await handleResetPassword(username);

      if (error) {
        handleResetError(error);
        return;
      }

      handleSuccessfulResend();
    } catch (error) {
      handleResetError(error);
    }
  };

  return (
    <form
      className="auth-form"
      onSubmit={confirmResetPasswordFormik.handleSubmit}
    >
      <div className="form-group">
        <h2>Reset Password</h2>
      </div>

      <TextInput
        label="Code *"
        id="resetPasswordCode"
        placeholder="Enter the code"
        getFieldProps={confirmResetPasswordFormik.getFieldProps}
        touched={confirmResetPasswordFormik.touched.resetPasswordCode}
        error={confirmResetPasswordFormik.errors.resetPasswordCode}
      />

      <PasswordInput
        label="New Password"
        id="newPassword"
        placeholder="Enter your password"
        getFieldProps={confirmResetPasswordFormik.getFieldProps}
        touched={confirmResetPasswordFormik.touched.newPassword}
        error={confirmResetPasswordFormik.errors.newPassword}
      />

      <PasswordInput
        label="Confirm Password"
        id="confirmNewPassword"
        placeholder="Confirm your password"
        getFieldProps={confirmResetPasswordFormik.getFieldProps}
        touched={confirmResetPasswordFormik.touched.confirmNewPassword}
        error={confirmResetPasswordFormik.errors.confirmNewPassword}
      />

      {errorMessage && <AlertMessage type="error">{errorMessage}</AlertMessage>}

      <Button
        type="submit"
        text="Submit"
        className="form-button"
        width="100%"
        variant="primary"
      />

      <Button
        type="button"
        text="Resend Code"
        onClick={resendCode}
        className="form-button"
        width="100%"
        variant="border"
      />
    </form>
  );
};

export default ConfirmResetPasswordForm;
